import React, { useState, useEffect } from 'react';
import matchService from '../services/matchService';
import tournamentService from '../services/tournamentService';
import { useTranslation } from 'react-i18next';
import { FiDownload, FiSearch, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import '../styles/AdminDashboard.css';

const ImportMatches = () => {
    const [leagueId, setLeagueId] = useState('');
    const [season, setSeason] = useState('');
    const [matches, setMatches] = useState([]);
    const [selectedMatches, setSelectedMatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [tournaments, setTournaments] = useState([]);
    const { t } = useTranslation();

    useEffect(() => {
        const fetchTournaments = async () => {
            try {
                const data = await tournamentService.getAllTournaments();
                setTournaments(data);
            } catch (error) {
                console.error('Error loading tournaments:', error);
            }
        };
        fetchTournaments();
    }, []);

    const fetchMatches = async () => {
        if (!leagueId) {
            setMessage({ type: 'error', text: 'Prosím vyberte turnaj.' });
            return;
        }

        try {
            setLoading(true);
            setMessage({ type: '', text: '' });
            const selectedTournament = tournaments.find(t => t.id.toString() === leagueId.toString());

            if (!selectedTournament) {
                setMessage({ type: 'error', text: 'Vybraný turnaj nebyl nalezen.' });
                setLoading(false);
                return;
            }

            // Podle sportu vybereme správné externí ID:
            //   FOOTBALL → apiFootballIdV3 (API-Football v3), nebo apiFootballTeamId (týmový turnaj)
            //   HOCKEY   → sportDbApiTournamentId (TheSportsDB)
            const sportType = selectedTournament.sportType;
            let queryParams = null;
            let sourceLabel = '';

            if (sportType === 'FOOTBALL') {
                if (selectedTournament.apiFootballTeamId) {
                    queryParams = { teamId: selectedTournament.apiFootballTeamId, season };
                } else if (selectedTournament.apiFootballIdV3) {
                    queryParams = { leagueId: selectedTournament.apiFootballIdV3, season };
                }
                sourceLabel = 'API-Football';
            } else if (sportType === 'HOCKEY') {
                if (selectedTournament.sportDbApiTournamentId) {
                    queryParams = { leagueId: selectedTournament.sportDbApiTournamentId, season };
                }
                sourceLabel = 'SportDB';
            } else {
                setMessage({
                    type: 'error',
                    text: `Vybraný turnaj má neznámý nebo nenastavený sport (${sportType ?? 'nevyplněno'}).`
                });
                setLoading(false);
                return;
            }

            if (!queryParams) {
                setMessage({
                    type: 'error',
                    text: `Vybraný turnaj nemá nastavené externí ID pro ${sourceLabel} (${sportType.toLowerCase()}).`
                });
                setLoading(false);
                return;
            }

            const data = await matchService.getMatchesFromExternal(queryParams);

            if (!data || data.length === 0) {
                 setMessage({ type: 'info', text: 'Pro zadanou sezónu nebyly nalezeny žádné zápasy.' });
            }

            setMatches(data || []);
            setSelectedMatches([]);
        } catch (error) {
            console.error('Failed to load matches:', error);
            setMessage({ type: 'error', text: 'Chyba při načítání zápasů z externího API.' });
        } finally {
            setLoading(false);
        }
    };

    const toggleSelectMatch = (match) => {
        setSelectedMatches(prev =>
            prev.includes(match) ? prev.filter(m => m !== match) : [...prev, match]
        );
    };

    const handleTournamentChange = (e) => {
        const selectedId = e.target.value;
        setLeagueId(selectedId);
        const selectedTournament = tournaments.find(t => t.id.toString() === selectedId.toString());

        if (selectedTournament && selectedTournament.year) {
            setSeason(selectedTournament.year);
        } else {
            setSeason('');
        }
    };

    const handleImport = async () => {
        try {
            setImporting(true);
            await matchService.importMatches({
                tournamentId: leagueId,
                matches: selectedMatches
            });
            setMessage({ type: 'success', text: `Úspěšně importováno ${selectedMatches.length} zápasů!` });
            setMatches([]); // Vyčistit po úspěšném importu
            setSelectedMatches([]);
        } catch (error) {
            console.error('Import failed:', error);
            setMessage({ type: 'error', text: 'Import selhal. Zkuste to prosím znovu.' });
        } finally {
            setImporting(false);
        }
    };

    const toggleSelectAll = () => {
        if (selectedMatches.length === matches.length) {
            setSelectedMatches([]);
        } else {
            setSelectedMatches(matches);
        }
    };

    return (
        <div className="container dashboard-container">
            <div className="card" style={{ padding: '20px', marginBottom: '20px' }}>
                <div className="form-row" style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div className="form-group" style={{ flex: 1, minWidth: '200px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Turnaj</label>
                        <select 
                            value={leagueId} 
                            onChange={handleTournamentChange}
                            className="form-control"
                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                        >
                            <option value="">-- Vyberte Turnaj --</option>
                            {tournaments.map(t => (
                                <option key={t.id} value={t.id}>
                                    {t.name} ({t.year})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group" style={{ flex: 1, minWidth: '150px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Sezóna</label>
                        <input 
                            value={season} 
                            onChange={(e) => setSeason(e.target.value)} 
                            placeholder="např. 2024-2025"
                            className="form-control"
                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                        />
                    </div>

                    <div className="form-group">
                        <button 
                            onClick={fetchMatches} 
                            disabled={loading || !leagueId}
                            className="btn-primary"
                            style={{ 
                                padding: '10px 20px', 
                                backgroundColor: '#007bff', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                opacity: (loading || !leagueId) ? 0.7 : 1
                            }}
                        >
                            {loading ? 'Načítám...' : <><FiSearch /> Vyhledat zápasy</>}
                        </button>
                    </div>
                </div>
            </div>

            {message.text && (
                <div className={`alert ${message.type}`} style={{
                    padding: '15px',
                    borderRadius: '6px',
                    marginBottom: '20px',
                    backgroundColor: message.type === 'error' ? '#fee2e2' : message.type === 'success' ? '#dcfce7' : '#e0f2fe',
                    color: message.type === 'error' ? '#991b1b' : message.type === 'success' ? '#166534' : '#075985',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    {message.type === 'error' && <FiAlertCircle />}
                    {message.type === 'success' && <FiCheckCircle />}
                    {message.text}
                </div>
            )}

            {matches.length > 0 && (
                <div className="card matches-results">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', padding: '0 10px' }}>
                        <h3>Nalezené zápasy ({matches.length})</h3>
                        <button 
                            onClick={handleImport} 
                            disabled={selectedMatches.length === 0 || importing}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                opacity: selectedMatches.length === 0 ? 0.5 : 1
                            }}
                        >
                            {importing ? 'Importuji...' : <><FiDownload /> Importovat vybrané ({selectedMatches.length})</>}
                        </button>
                    </div>

                    <div className="table-responsive" style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f8f9fa', textAlign: 'left' }}>
                                    <th style={{ padding: '12px', borderBottom: '2px solid #dee2e6' }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedMatches.length === matches.length}
                                            onChange={() => toggleSelectAll()}
                                            style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                        />
                                    </th>
                                    <th style={{ padding: '12px', borderBottom: '2px solid #dee2e6' }}>Domácí</th>
                                    <th style={{ padding: '12px', borderBottom: '2px solid #dee2e6' }}>Hosté</th>
                                    <th style={{ padding: '12px', borderBottom: '2px solid #dee2e6' }}>Datum a Čas</th>
                                    <th style={{ padding: '12px', borderBottom: '2px solid #dee2e6' }}>Kolo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {matches.map((match, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '12px' }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedMatches.includes(match)}
                                                onChange={() => toggleSelectMatch(match)}
                                                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                            />
                                        </td>
                                        <td style={{ padding: '12px', fontWeight: '500' }}>{match.strHomeTeam}</td>
                                        <td style={{ padding: '12px', fontWeight: '500' }}>{match.strAwayTeam}</td>
                                        <td style={{ padding: '12px', color: '#666' }}>
                                            {new Date(match.strTimestamp).toLocaleString('cs-CZ')}
                                        </td>
                                        <td style={{ padding: '12px', color: '#666' }}>{match.intRound}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImportMatches;